import { useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { usePipelineMatches, usePipelineScenarios } from '@/lib/admin/pipeline-queries';
import type { PipelineMatchRow } from '@/lib/admin/pipeline-queries';
import { useConfirmMatches } from '@/lib/admin/pipeline-mutations';
import type { MatchSelection } from '@/lib/admin/pipeline-mutations';
import { DIFFICULTY_LABEL } from '@/lib/admin/pipeline-labels';

type Difficulty = 'easy' | 'medium' | 'hard';
interface Decision {
  selected: boolean;
  difficulty: Difficulty;
}

export function TutorAdminPipelineMatchingsPage() {
  const { runId } = useParams({ strict: false }) as { runId: string };
  const navigate = useNavigate();
  const { data: matches, isLoading } = usePipelineMatches(runId);
  const { data: scenarios } = usePipelineScenarios(runId);
  const confirm = useConfirmMatches();

  // Décisions locales : proposé → sélectionné par défaut, auto-rejeté → exclu.
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [showDiscarded, setShowDiscarded] = useState(false);

  const decisionOf = (m: PipelineMatchRow): Decision =>
    decisions[m.id] ?? {
      selected: m.status === 'proposed' || m.status === 'selected',
      difficulty: m.difficulty,
    };

  function setDecision(id: string, patch: Partial<Decision>, current: Decision) {
    setDecisions((prev) => ({ ...prev, [id]: { ...current, ...patch } }));
  }

  const scenarioTitle = useMemo(() => {
    const map = new Map((scenarios ?? []).map((s) => [s.id, s.title]));
    return (id: string) => map.get(id) ?? 'Scénario';
  }, [scenarios]);

  const proposed = (matches ?? []).filter((m) => m.status !== 'auto_discarded');
  const autoDiscarded = (matches ?? []).filter((m) => m.status === 'auto_discarded');

  const selectedCount = (matches ?? []).filter((m) => decisionOf(m).selected).length;

  // Regroupe les matchs proposés par scénario.
  const groups = useMemo(() => {
    const byScenario = new Map<string, PipelineMatchRow[]>();
    for (const m of proposed) {
      const list = byScenario.get(m.scenario_id) ?? [];
      list.push(m);
      byScenario.set(m.scenario_id, list);
    }
    return [...byScenario.entries()];
  }, [proposed]);

  async function handleGo() {
    if (selectedCount === 0 || confirm.isPending) return;
    const selections: MatchSelection[] = (matches ?? []).map((m) => {
      const d = decisionOf(m);
      return {
        matchId: m.id,
        action: d.selected ? 'select' : 'discard',
        overriddenDifficulty: d.difficulty !== m.difficulty ? d.difficulty : undefined,
      };
    });
    await confirm.mutateAsync({ runId, selections });
    navigate({ to: '/tutor/admin/pipeline' });
  }

  return (
    <div className="mx-auto max-w-3xl p-4 pb-28 sm:p-6">
      <Link
        to="/tutor/admin/pipeline"
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Pipeline
      </Link>

      <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
        Sélectionner les matchings
      </h1>
      <p className="text-ink-500 mt-1 font-sans text-sm">
        Étape 6 — choisis les exercices à générer et ajuste leur difficulté.
      </p>

      {isLoading ? (
        <div className="mt-6 space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="bg-ink-100 h-32 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {groups.map(([scenarioId, list]) => (
            <div key={scenarioId}>
              <p className="font-display text-ink-400 mb-2 text-[11px] font-bold uppercase tracking-widest">
                {scenarioTitle(scenarioId)}
              </p>
              <div className="space-y-2.5">
                {list.map((m) => (
                  <MatchRow
                    key={m.id}
                    match={m}
                    decision={decisionOf(m)}
                    onChange={(patch) => setDecision(m.id, patch, decisionOf(m))}
                  />
                ))}
              </div>
            </div>
          ))}

          {autoDiscarded.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowDiscarded((v) => !v)}
                className="text-ink-500 hover:text-ink-900 flex min-h-11 items-center gap-1.5 font-sans text-[13px]"
              >
                <ChevronDown
                  className={cn('h-4 w-4 transition-transform', showDiscarded && 'rotate-180')}
                />
                {autoDiscarded.length} matching(s) auto-rejeté(s) (scores ≤ 5)
              </button>
              {showDiscarded && (
                <div className="mt-2 space-y-2.5">
                  {autoDiscarded.map((m) => (
                    <MatchRow
                      key={m.id}
                      match={m}
                      decision={decisionOf(m)}
                      onChange={(patch) => setDecision(m.id, patch, decisionOf(m))}
                      dimmed
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {confirm.isError && (
        <p className="text-accent-600 mt-4 font-sans text-[13px]">{confirm.error?.message}</p>
      )}

      <div className="border-ink-200 fixed inset-x-0 bottom-0 border-t bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:left-60">
        <div className="mx-auto max-w-3xl">
          <Button
            onClick={handleGo}
            disabled={selectedCount === 0 || confirm.isPending}
            className="min-h-11 w-full"
          >
            {confirm.isPending
              ? 'Lancement…'
              : `Générer ${selectedCount} exercice${selectedCount !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function MatchRow({
  match,
  decision,
  onChange,
  dimmed,
}: {
  match: PipelineMatchRow;
  decision: Decision;
  onChange: (patch: Partial<Decision>) => void;
  dimmed?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-2xl border bg-white p-3.5 transition-colors',
        decision.selected ? 'border-primary-200 bg-primary-50/40' : 'border-ink-200',
        dimmed && !decision.selected && 'opacity-70',
      )}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={decision.selected}
          onClick={() => onChange({ selected: !decision.selected })}
          className={cn(
            'mt-0.5 flex h-7 w-12 shrink-0 items-center rounded-full px-0.5 transition-colors',
            decision.selected ? 'bg-primary-600' : 'bg-ink-200',
          )}
        >
          <span
            className={cn(
              'h-6 w-6 rounded-full bg-white shadow transition-transform',
              decision.selected && 'translate-x-5',
            )}
          />
        </button>

        <div className="min-w-0 flex-1">
          <p className="font-display text-ink-900 text-[14px] font-bold">
            {match.chapter?.title ?? 'Chapitre'}
            {match.lesson?.title && (
              <span className="text-ink-500 font-sans font-normal"> · {match.lesson.title}</span>
            )}
          </p>
          <div className="text-ink-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[11px]">
            <span>{match.level}</span>
            <span>·</span>
            <span>pertinence {match.relevance_score}</span>
            <span>·</span>
            <span>intérêt {match.interest_score}</span>
          </div>
          {match.reasoning && (
            <p className="text-ink-400 mt-1 line-clamp-2 font-sans text-[12px]">
              {match.reasoning}
            </p>
          )}
        </div>

        <select
          value={decision.difficulty}
          onChange={(e) => onChange({ difficulty: e.target.value as Difficulty })}
          className="border-ink-200 text-ink-700 focus:ring-primary-400 h-9 shrink-0 rounded-lg border bg-white px-2 font-sans text-[12px] focus:outline-none focus:ring-2"
        >
          {(['easy', 'medium', 'hard'] as const).map((d) => (
            <option key={d} value={d}>
              {DIFFICULTY_LABEL[d]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
